import React, { useState, useEffect } from 'react';
import { submitJarTip } from '../services/jarTipService';
import { getStaff, Staff } from '../services/staffService';
import { getShifts, Shift } from '../services/shiftService';
import { getPlatformConfig } from '../services/platformConfigService';
import { useFirebase } from '../contexts/FirebaseContext';
import { User, Clock, DollarSign, Calendar, Zap } from 'lucide-react';
import { getLocalDate } from '../lib/utils';
import { getSettings } from '../services/settingsService';
import { useStoreLocation } from '../contexts/LocationContext';

interface Props {
  tenantId: string;
}

export const JarTipForm: React.FC<Props> = ({ tenantId }) => {
  const { user } = useFirebase();
  const { activeLocationId, activeLocation } = useStoreLocation();
  const [formData, setFormData] = useState({
    submitterName: '',
    email: user?.email || '',
    jarAmount: '' as string | number,
    tipDate: '',
    shift: '',
    payMethod: 'Paycheck',
    paidDate: '',
    notes: ''
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [payoutMethods, setPayoutMethods] = useState<string[]>(['Paycheck', 'Cash', 'Zelle', 'PayPal/Venmo', 'Other']);
  const [shiftList, setShiftList] = useState<Shift[]>([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [staff, shifts, settingsData, payoutConfig] = await Promise.all([
        getStaff(tenantId),
        getShifts(tenantId),
        getSettings(tenantId),
        getPlatformConfig('payoutMethods')
      ]);
      setSettings(settingsData);
      if (payoutConfig) {
        setPayoutMethods(payoutConfig.options);
      }

      const timezone = settingsData?.timezone || 'UTC';
      const today = getLocalDate(timezone);
      setShiftList(shifts);
      setStaffList(staff);
      
      setFormData(prev => ({ 
        ...prev, 
        tipDate: today,
        payMethod: settingsData?.payoutMethod || 'Paycheck'
      }));

      if (user?.email) {
        const currentUserStaff = staff.find(s => s.email === user.email);
        if (currentUserStaff) {
          setFormData(prev => ({ ...prev, submitterName: currentUserStaff.name, email: user.email! }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            submitterName: user.displayName || user.email!.split('@')[0], 
            email: user.email! 
          }));
        }
      }
      setLoading(false);
    };
    init();
  }, [tenantId, user]);

  if (loading) {
    return (
      <div className="card p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
        <p className="text-gray-500 font-medium">Loading form...</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.submitterName || !formData.email) {
      setStatus('Error: Submitter information is required.');
      return;
    }

    try {
      const numericAmount = typeof formData.jarAmount === 'string' ? parseFloat(formData.jarAmount) || 0 : formData.jarAmount;

      const selectedStaff = staffList.find(s => s.email === formData.email);

      if (!selectedStaff) {
        setStatus('Error: Please select a valid staff member.');
        return;
      }

      await submitJarTip(tenantId, {
        tenantId,
        staffId: selectedStaff.id,
        posId: selectedStaff.posId || null,
        submitterName: formData.submitterName,
        email: formData.email,
        jarAmount: numericAmount,
        tipDate: formData.tipDate,
        shift: formData.shift,
        status: 'Pending',
        paymentStatus: formData.payMethod === 'Paycheck' ? 'Unpaid' : 'Paid',
        payoutMethod: formData.payMethod,
        source: 'Jar',
        masterTxId: 'unknown',
        locationId: activeLocationId && activeLocationId !== 'all' ? activeLocationId : (selectedStaff.locationId || 'loc-downtown'),
        locationName: activeLocation?.name || selectedStaff.locationName || 'Downtown Bistro'
      });
      const settings = await getSettings(tenantId);
      const timezone = settings?.timezone || 'UTC';
      const today = getLocalDate(timezone);
      
      setStatus('Jar tips submitted successfully!');
      setFormData({
        submitterName: '',
        email: user?.email || '',
        jarAmount: '',
        tipDate: today,
        shift: '',
        payMethod: settings?.payoutMethod || 'Paycheck',
        paidDate: '',
        notes: ''
      });
    } catch (error) {
      console.error('Submit error:', error);
      setStatus('Error submitting jar tips.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 md:p-8 max-w-2xl mx-auto space-y-8">
      <div className="space-y-6">
        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-400 shadow-sm shrink-0">
            <User size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reported By (Lead / Staff Profile)</label>
            <div className="mt-1">
              <select
                value={formData.email}
                onChange={e => {
                  const selected = staffList.find(s => s.email.toLowerCase() === e.target.value.toLowerCase());
                  setFormData({
                    ...formData,
                    email: e.target.value,
                    submitterName: selected?.name || formData.submitterName
                  });
                }}
                className="input-field h-11 text-sm font-bold w-full bg-white"
              >
                <option value="">Select Submitter...</option>
                {staffList.map(s => (
                  <option key={s.id || s.email} value={s.email}>
                    {s.name} {s.posId ? `[POS ID: ${s.posId}]` : ''} {s.id ? `[Staff ID: ${s.id.slice(0, 8)}]` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Resolved staff identification badges */}
            {(() => {
              const matched = staffList.find(s => s.email.toLowerCase() === formData.email.toLowerCase() || s.name.toLowerCase() === formData.submitterName.toLowerCase());
              return matched ? (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {matched.posId && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase tracking-wider">
                      POS ID: {matched.posId}
                    </span>
                  )}
                  {matched.id && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold tracking-wider" title={`Full Staff ID: ${matched.id}`}>
                      Staff ID: {matched.id.slice(0, 8)}
                    </span>
                  )}
                  {matched.role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
                      Role: {matched.role}
                    </span>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest">
              <Calendar size={14} className="text-blue-500" />
              Service Date
            </label>
            <input 
              type="date" 
              value={formData.tipDate} 
              onChange={e => setFormData({...formData, tipDate: e.target.value})} 
              className="input-field h-14 text-base font-bold" 
              required 
            />
          </div>
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest">
              <Clock size={14} className="text-blue-500" />
              Service Shift
            </label>
            <select 
              value={formData.shift} 
              onChange={e => setFormData({...formData, shift: e.target.value})} 
              className="input-field h-14 text-base font-bold"
              required
            >
              <option key="default-shift" value="">Select Window...</option>
              {shiftList.map((s, index) => (
                <option key={s.id || `shift-${index}`} value={s.shiftName}>{s.shiftName}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest">
              <DollarSign size={14} className="text-emerald-500" />
              Jar Total ($)
            </label>
            <input 
              type="number" 
              step="0.01"
              placeholder="0.00" 
              value={formData.jarAmount} 
              onChange={e => setFormData({...formData, jarAmount: e.target.value})} 
              onWheel={e => e.currentTarget.blur()}
              className="input-field h-14 font-black text-2xl text-emerald-700" 
              required 
            />
          </div>
          {settings?.allowPayoutOverride ? (
            <div className="space-y-4">
              <label className="block text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Payout Method</label>
              <select
                value={formData.payMethod}
                onChange={e => setFormData({...formData, payMethod: e.target.value})}
                className="input-field h-14 text-base font-bold shadow-sm"
              >
                {payoutMethods.map(method => <option key={method} value={method}>{method}</option>)}
              </select>
            </div>
          ) : (
            <div className="p-4 bg-green-50 rounded-xl border border-green-100 flex justify-between items-center">
                <div>
                  <p className="text-[10px] font-bold text-green-400 uppercase tracking-widest">Payout Method (Company Default)</p>
                  <p className="text-sm font-bold text-green-700">{formData.payMethod === 'Paycheck' ? 'Add to next paycheck' : `Already paid - ${formData.payMethod}`}</p>
                </div>
                <div className="text-green-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">Notes (Optional)</label>
          <textarea 
            placeholder="Add any additional details..." 
            value={formData.notes} 
            onChange={e => setFormData({...formData, notes: e.target.value})} 
            className="input-field min-h-[100px] py-3" 
          />
        </div>

        <button type="submit" className="w-full h-20 bg-green-600 hover:bg-green-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-[2rem] text-xl font-black uppercase italic tracking-widest flex items-center justify-center gap-4 shadow-2xl shadow-green-600/30 transition-all active:scale-[0.98] tap-highlight-transparent">
          <Zap size={24} />
          Submit Jar Tips
        </button>
      </div>

      {status && (
        <div className={`mt-6 p-4 rounded-xl text-center font-medium ${status.includes('Error') ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'}`}>
          {status}
        </div>
      )}
    </form>
  );
};
